/**
 * Authentication service — manages auth flows, token lifecycle, and auth state.
 *
 * Singleton pattern, consistent with other services (themeManager, compassService, etc.).
 *
 * Token storage:
 *  - Access/ID tokens: in-memory only (never persisted to disk)
 *  - Refresh token: stored in Dexie `authTokens` table for session persistence
 *
 * Auth flows:
 *  - Email OTP: InitiateAuth → Cognito sends code → RespondToAuthChallenge → tokens
 *  - Passkey: InitiateAuth → WebAuthn challenge → biometric → RespondToAuthChallenge → tokens
 *  - Google OAuth: redirect to /oauth2/authorize?identity_provider=Google → callback → code exchange
 */

import { logger } from '../../utils/logger.js'
import { Capacitor } from '@capacitor/core'
import { authConfig, loadAuthConfig } from './auth-config.js'
import { type AuthState, GUEST_STATE, GUEST_USER_ID } from './auth-context.js'
import {
  generatePkce,
  storeVerifier,
  retrieveVerifier,
  generateState,
  validateState,
} from './pkce.js'
import { db } from '../../database/schema.js'
import { migrateGuestData } from './data-migration.js'
import {
  type AuthChallengeResult,
  type AuthenticationResult,
  initiateAuth,
  respondToAuthChallenge,
  signUp,
  confirmSignUp,
  resendConfirmationCode,
} from './cognito-api.js'

const log = logger.create('auth')

const CACHED_AUTH_LS_KEY = 'cairn_cached_auth'

/** Refresh 60 seconds before expiry */
const REFRESH_BUFFER_MS = 60_000

interface TokenResponse {
  access_token: string
  id_token: string
  refresh_token?: string | undefined
  expires_in: number
  token_type: string
}

export interface IdTokenClaims {
  sub: string
  email?: string
  name?: string
  given_name?: string
  family_name?: string
  picture?: string
  phone_number?: string
  birthdate?: string
  email_verified?: boolean
  'cognito:username'?: string
}

export function parseIdToken(idToken: string): IdTokenClaims {
  const payload = idToken.split('.')[1]
  const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
  return JSON.parse(decoded)
}

export interface AuthServiceDeps {
  db: {
    authTokens: {
      get: (id: string) => Promise<{ value: string } | undefined>
      put: (record: { id: string; value: string }) => Promise<unknown>
      delete: (id: string) => Promise<void>
    }
    users: {
      get: (id: string) => Promise<Record<string, unknown> | undefined>
      update: (id: string, data: Record<string, unknown>) => Promise<unknown>
      add: (record: unknown) => Promise<unknown>
      toArray: () => Promise<Record<string, unknown>[]>
    }
    syncEvents: { add: (record: unknown) => Promise<unknown> }
    table: (name: string) => unknown
    transaction: (mode: string, tables: unknown[], fn: () => Promise<void>) => Promise<void>
  }
  cognito: {
    initiateAuth: typeof initiateAuth
    respondToAuthChallenge: typeof respondToAuthChallenge
    signUp: typeof signUp
    confirmSignUp: typeof confirmSignUp
    resendConfirmationCode: typeof resendConfirmationCode
  }
  pkce: {
    generatePkce: typeof generatePkce
    storeVerifier: typeof storeVerifier
    retrieveVerifier: typeof retrieveVerifier
    generateState: typeof generateState
    validateState: typeof validateState
  }
  config: typeof authConfig & { loadAuthConfig: typeof loadAuthConfig }
  migrateGuestData: typeof migrateGuestData
  fetch: typeof globalThis.fetch
  isNative: boolean
}

function defaultDeps(): AuthServiceDeps {
  return {
    db: db as unknown as AuthServiceDeps['db'],
    cognito: {
      initiateAuth,
      respondToAuthChallenge,
      signUp,
      confirmSignUp,
      resendConfirmationCode,
    },
    pkce: { generatePkce, storeVerifier, retrieveVerifier, generateState, validateState },
    config: Object.assign(authConfig, { loadAuthConfig }),
    migrateGuestData,
    fetch: globalThis.fetch.bind(globalThis),
    isNative: Capacitor.isNativePlatform(),
  }
}

export class AuthService {
  private state: AuthState = { ...GUEST_STATE }
  private accessToken: string | null = null
  _idToken: string | null = null
  private refreshTimer: ReturnType<typeof setTimeout> | null = null
  private listeners = new Set<(state: AuthState) => void>()
  private passkeyCompleteCallback: ((success: boolean) => void) | null = null
  private deps: AuthServiceDeps
  private _hasPriorSession = false
  private callbackInProgress = false
  private browserOpenedForAuth = false

  constructor(deps?: AuthServiceDeps) {
    this.deps = deps ?? defaultDeps()
    this.restoreCachedStateSync()
  }

  private restoreCachedStateSync(): void {
    try {
      const cached = localStorage.getItem(CACHED_AUTH_LS_KEY)
      if (cached) {
        const state: AuthState = JSON.parse(cached)
        if (state.status === 'authenticated' && state.userId) {
          this.state = state
          this._hasPriorSession = true
        }
      }
    } catch {}
  }

  /** Subscribe to auth state changes. Returns an unsubscribe function. */
  onChange(listener: (state: AuthState) => void): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  /** Register a one-shot callback for passkey registration completion (deep link). */
  onPasskeyComplete(callback: (success: boolean) => void): void {
    this.passkeyCompleteCallback = callback
  }

  getState(): AuthState {
    return this.state
  }

  /** Get the current user ID (guest UUID or Cognito sub). */
  getCurrentUserId(): string {
    return this.state.userId
  }

  /** Get the access token for API calls, or null if not authenticated. */
  getAccessToken(): string | null {
    return this.accessToken
  }

  /** True if a refresh token exists — evidence of a prior authenticated session. */
  get hasPriorSession(): boolean {
    return this._hasPriorSession
  }

  /**
   * Initialize auth on app startup.
   * Loads runtime config, restores cached identity for instant local access,
   * then attempts a background token refresh (non-blocking).
   */
  async initialize(): Promise<void> {
    await this.deps.config.loadAuthConfig()

    if (!this.deps.config.isConfigured) {
      log.debug('Auth not configured, staying in guest mode')
      return
    }

    // On native, listen for deep link callbacks from the system browser
    if (this.deps.isNative) {
      void this.registerDeepLinkListener()
    }

    // Restore cached identity immediately — no network required.
    // This gives the app a known user context for local operations
    // (querying Dexie, showing user info, skipping login modal).
    await this.restoreCachedState()

    // Refresh access token in the background — don't block app startup.
    try {
      const stored = await this.deps.db.authTokens.get('refresh')
      if (stored?.value) {
        log.debug('Found stored refresh token, attempting background refresh')
        void this.refreshAccessToken(stored.value).catch(error => {
          log.debug('Background token refresh failed:', error)
        })
      }
    } catch (error) {
      log.debug('No stored session to restore:', error)
    }
  }

  private async registerDeepLinkListener(): Promise<void> {
    try {
      const { App } = await import('@capacitor/app')
      void App.addListener('appUrlOpen', ({ url }) => {
        log.debug('appUrlOpen:', url)

        if (!url.startsWith('com.taselith.app://') && !url.startsWith('https://')) {
          return
        }

        const parsed = new URL(url)
        const isCallback =
          parsed.pathname === '/callback' ||
          parsed.pathname === '/auth/callback' ||
          parsed.pathname === '//auth/callback'
        if (isCallback) {
          void this.handleDeepLinkCallback(parsed.searchParams)
          return
        }

        const isPasskeyComplete =
          parsed.pathname === '/passkey-complete' ||
          parsed.pathname === '/auth/passkey-complete' ||
          parsed.pathname === '//auth/passkey-complete'
        if (isPasskeyComplete) {
          void this.handlePasskeyComplete(parsed.searchParams)
        }
      })
    } catch {
      log.warn('Could not register deep link listener')
    }
  }

  /** Handle passkey registration completion deep link from Chrome Custom Tab. */
  private async handlePasskeyComplete(searchParams: URLSearchParams): Promise<void> {
    if (this.browserOpenedForAuth) {
      this.browserOpenedForAuth = false
      try {
        const { Browser } = await import('@capacitor/browser')
        await Browser.close()
      } catch {
        /* browser may already be closed */
      }
    }

    const success = searchParams.get('success') === 'true'
    log.debug('Passkey registration complete:', success)

    if (this.passkeyCompleteCallback) {
      this.passkeyCompleteCallback(success)
      this.passkeyCompleteCallback = null
    }
  }

  /** Handle OAuth callback received via native deep link. */
  private async handleDeepLinkCallback(searchParams: URLSearchParams): Promise<void> {
    // Signal authenticating immediately so the login modal doesn't flash
    this.setState({ ...this.state, status: 'authenticating' })

    // Only close the browser if we opened it for auth in this session
    if (this.browserOpenedForAuth) {
      this.browserOpenedForAuth = false
      try {
        const { Browser } = await import('@capacitor/browser')
        await Browser.close()
      } catch {
        /* browser may already be closed */
      }
    }

    const success = await this.handleCallback(searchParams)
    if (success) {
      const { router } = await import('../../utils/router.js')
      if (this.needsSetup()) {
        router.navigate('/auth/setup')
      } else if (this.needsEula()) {
        router.navigate('/auth/eula')
      } else {
        router.navigate('/')
      }
    }
  }

  // ── Login methods ──

  /** Navigate to the login page. */
  async login(): Promise<void> {
    const { router } = await import('../../utils/router.js')
    router.navigate('/auth/login')
  }

  /**
   * Start email OTP login. Cognito sends a 6-digit code to the email.
   * Returns the challenge result with session token needed for verifyOtp().
   */
  async loginWithEmailOtp(email: string): Promise<AuthChallengeResult> {
    this.setState({ ...GUEST_STATE, status: 'authenticating' })
    return this.deps.cognito.initiateAuth(email, 'EMAIL_OTP')
  }

  /**
   * Start passkey login. Triggers native biometric prompt via Credential Manager.
   * On success, processes tokens and completes authentication.
   */
  async loginWithPasskey(email: string): Promise<void> {
    this.setState({ ...GUEST_STATE, status: 'authenticating' })

    try {
      // 1. Get WebAuthn challenge from Cognito
      const challenge = await this.deps.cognito.initiateAuth(email, 'WEB_AUTHN')

      if (
        challenge.challengeName !== 'WEB_AUTHN' ||
        !challenge.session ||
        !challenge.challengeParameters
      ) {
        throw new Error('Unexpected challenge type for passkey login')
      }

      // 2. Run WebAuthn assertion via native plugin
      const { CairnPasskeys } = await import('cairn-passkeys')
      const requestJson = challenge.challengeParameters.CredentialRequestOptions
      const { responseJson } = await CairnPasskeys.getCredential({ requestJson })

      // 3. Complete auth with the assertion
      const result = await this.deps.cognito.respondToAuthChallenge(
        'WEB_AUTHN',
        challenge.session,
        {
          USERNAME: email,
          CREDENTIAL: responseJson,
        }
      )

      if (!result.authenticationResult) {
        throw new Error('Passkey authentication did not return tokens')
      }

      await this.processAuthResult(result.authenticationResult)
      this.cacheEmail(email)
    } catch (err) {
      log.error('Passkey login failed:', err)
      this.setState(GUEST_STATE)
      throw err
    }
  }

  /**
   * Verify an email OTP code. Completes the EMAIL_OTP challenge from loginWithEmailOtp().
   */
  async verifyOtp(session: string, email: string, code: string): Promise<void> {
    try {
      const result = await this.deps.cognito.respondToAuthChallenge('EMAIL_OTP', session, {
        USERNAME: email,
        EMAIL_OTP_CODE: code,
      })

      if (!result.authenticationResult) {
        throw new Error('OTP verification did not return tokens')
      }

      await this.processAuthResult(result.authenticationResult)
      this.cacheEmail(email)
    } catch (err) {
      // Re-throw so the UI can show the error, but reset state on non-retryable errors
      if (err instanceof Error && !err.message.includes('CodeMismatch')) {
        this.setState(GUEST_STATE)
      }
      throw err
    }
  }

  /**
   * Redirect to Google OAuth via Cognito's authorize endpoint.
   * Skips Managed Login by passing identity_provider=Google directly.
   */
  async loginWithGoogle(): Promise<void> {
    this.setState({ ...GUEST_STATE, status: 'authenticating' })

    const { codeVerifier, codeChallenge } = await this.deps.pkce.generatePkce()
    this.deps.pkce.storeVerifier(codeVerifier)
    const state = this.deps.pkce.generateState()

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: this.deps.config.clientId,
      redirect_uri: this.deps.config.redirectUri,
      scope: this.deps.config.scopes,
      state,
      code_challenge_method: 'S256',
      code_challenge: codeChallenge,
      identity_provider: 'Google',
    })

    const url = `${this.deps.config.authorizeUrl}?${params}`

    if (this.deps.isNative) {
      try {
        const { Browser } = await import('@capacitor/browser')
        this.browserOpenedForAuth = true
        await Browser.open({ url, presentationStyle: 'popover' })
      } catch (err) {
        log.error('Failed to open browser for Google login:', err)
        this.setState(GUEST_STATE)
      }
    } else {
      window.location.href = url
    }
  }

  // ── Registration methods ──

  /** Register a new user with email (passwordless). Cognito sends a verification code. */
  async register(email: string): Promise<string | undefined> {
    const result = await this.deps.cognito.signUp(email)
    return result.session
  }

  /**
   * Confirm registration and auto-sign-in.
   * Passes the session from SignUp → ConfirmSignUp → InitiateAuth
   * so Cognito authenticates without sending another OTP.
   */
  async confirmRegistration(email: string, code: string, signUpSession?: string): Promise<void> {
    const confirmResult = await this.deps.cognito.confirmSignUp(email, code, signUpSession)

    // Auto-sign-in using the session from ConfirmSignUp
    if (confirmResult.session) {
      const authResult = await this.deps.cognito.initiateAuth(
        email,
        undefined,
        confirmResult.session
      )
      if (authResult.authenticationResult) {
        await this.processAuthResult(authResult.authenticationResult)
        this.cacheEmail(email)
        return
      }
    }

    // Fallback: session not available, caller should redirect to login
    log.warn('Auto-sign-in after registration not available — no session returned')
  }

  /** Resend the registration confirmation code. */
  async resendRegistrationCode(email: string): Promise<void> {
    await this.deps.cognito.resendConfirmationCode(email)
  }

  // ── Post-login helpers ──

  /** Check if the user needs to complete account setup (first login). */
  needsSetup(): boolean {
    // If no display name set, this is likely a first login
    return this.state.status === 'authenticated' && !this.state.displayName
  }

  /** Check if the user needs to accept the EULA before using the app. */
  needsEula(): boolean {
    if (this.state.status !== 'authenticated') return false
    try {
      return !localStorage.getItem(`cairn_eula_accepted_${this.state.userId}`)
    } catch {
      return true
    }
  }

  /** Record EULA acceptance locally and on the server. */
  async acceptEula(): Promise<void> {
    const userId = this.state.userId
    const { environment } = await import('../../environments/environment.prod.js')
    const token = this.getAccessToken()
    if (!token) throw new Error('Not authenticated')

    const res = await this.deps.fetch(`${environment.api.baseUrl}/api/user/accept-eula`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${token}`,
      },
    })
    if (!res.ok) throw new Error(`EULA acceptance failed: ${res.status}`)

    try {
      localStorage.setItem(`cairn_eula_accepted_${userId}`, new Date().toISOString())
    } catch {
      // localStorage may be unavailable, but server-side is saved
    }
  }

  /** Force a token refresh to pick up updated Cognito attributes. */
  async refreshSession(): Promise<void> {
    const stored = await this.deps.db.authTokens.get('refresh')
    if (stored?.value) {
      await this.refreshAccessToken(stored.value)
    }
  }

  /** Get the last authenticated email for pre-filling login forms. */
  getLastEmail(): string | null {
    try {
      return localStorage.getItem('cairn_last_email')
    } catch {
      return null
    }
  }

  private cacheEmail(email: string): void {
    try {
      localStorage.setItem('cairn_last_email', email)
    } catch {
      // localStorage may be unavailable
    }
  }

  /**
   * Handle the OAuth callback after redirect from Cognito.
   * Validates state, exchanges the authorization code for tokens.
   * Returns true on success, false on failure.
   */
  async handleCallback(searchParams: URLSearchParams): Promise<boolean> {
    if (this.callbackInProgress) {
      log.debug('Duplicate callback blocked')
      return false
    }
    this.callbackInProgress = true

    if (!this.deps.config.isConfigured) {
      await this.deps.config.loadAuthConfig()
    }

    const code = searchParams.get('code')
    const state = searchParams.get('state')
    const error = searchParams.get('error')

    if (error) {
      log.warn('OAuth error:', error)
      this.setState(GUEST_STATE)
      return false
    }

    if (!code || !state) {
      log.debug('Callback missing code or state')
      this.setState(GUEST_STATE)
      return false
    }

    if (!this.deps.pkce.validateState(state)) {
      log.warn('OAuth state mismatch')
      this.setState(GUEST_STATE)
      return false
    }

    const codeVerifier = this.deps.pkce.retrieveVerifier()
    if (!codeVerifier) {
      log.warn('Missing PKCE verifier')
      this.setState(GUEST_STATE)
      return false
    }

    this.setState({ ...this.state, status: 'authenticating' })

    try {
      const tokens = await this.exchangeCode(code, codeVerifier)
      await this.processTokens(tokens)
      return true
    } catch (err) {
      log.error('Token exchange failed:', err)
      this.setState(GUEST_STATE)
      return false
    } finally {
      this.callbackInProgress = false
    }
  }

  /** Sign out: revoke refresh token, clear local state. */
  async logout(): Promise<void> {
    // Revoke the refresh token if we have one
    try {
      const stored = await this.deps.db.authTokens.get('refresh')
      if (stored?.value) {
        await this.revokeToken(stored.value)
      }
    } catch (error) {
      log.error('Failed to revoke token:', error)
    }

    // Clear everything locally — no server-side session to clear
    // since we use direct Cognito API calls (not Managed Login)
    this.clearSession()
  }

  // ── Private methods ──

  /**
   * Process tokens from Cognito's InitiateAuth/RespondToAuthChallenge response.
   * These use PascalCase (AccessToken, IdToken) vs the OAuth token endpoint's snake_case.
   */
  private async processAuthResult(result: AuthenticationResult): Promise<void> {
    const tokens: TokenResponse = {
      access_token: result.AccessToken,
      id_token: result.IdToken,
      refresh_token: result.RefreshToken,
      expires_in: result.ExpiresIn,
      token_type: result.TokenType,
    }
    await this.processTokens(tokens)
  }

  private async exchangeCode(code: string, codeVerifier: string): Promise<TokenResponse> {
    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: this.deps.config.clientId,
      code,
      redirect_uri: this.deps.config.redirectUri,
      code_verifier: codeVerifier,
    })

    const response = await this.deps.fetch(this.deps.config.tokenUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    })

    if (!response.ok) {
      const text = await response.text()
      throw new Error(`Token exchange failed: ${response.status} ${text}`)
    }

    return response.json()
  }

  private async processTokens(tokens: TokenResponse): Promise<void> {
    this.accessToken = tokens.access_token
    this._idToken = tokens.id_token

    // Store refresh token in Dexie
    if (tokens.refresh_token) {
      await this.deps.db.authTokens.put({ id: 'refresh', value: tokens.refresh_token })
    }

    // Parse ID token claims (unverified — server validates via JWT authorizer)
    const claims = this.parseIdToken(tokens.id_token)
    log.debug('ID token claims:', claims)

    const newState: AuthState = {
      status: 'authenticated',
      userId: claims.sub,
    }
    if (claims.email) newState.email = claims.email
    if (claims.name) newState.displayName = claims.name
    if (claims.given_name) newState.givenName = claims.given_name
    if (claims.family_name) newState.familyName = claims.family_name
    if (claims.picture) newState.picture = claims.picture
    if (claims.phone_number) newState.phoneNumber = claims.phone_number
    if (claims.birthdate) newState.birthdate = claims.birthdate

    // Cache email for pre-filling login forms
    if (claims.email) {
      this.cacheEmail(claims.email)
    }

    // Migrate any guest-owned data to this user
    await this.deps.migrateGuestData(claims.sub)

    // Ensure user profile exists in local DB (and will be pushed to server)
    // Must run before setState so the sync event exists when the auth listener triggers push
    await this.ensureUserRecord(claims)

    this.setState(newState)
    await this.cacheAuthState(newState)

    // Schedule silent refresh
    this.scheduleRefresh(tokens.expires_in)

    log.info('Authenticated as', claims.sub)
  }

  private async ensureUserRecord(claims: IdTokenClaims): Promise<void> {
    try {
      const existing = await this.deps.db.users.get(claims.sub)
      const publicData: Record<string, unknown> = {}
      if (claims.name) publicData.displayName = claims.name
      if (claims.given_name) publicData.givenName = claims.given_name
      if (claims.family_name) publicData.familyName = claims.family_name
      if (claims.picture) publicData.picture = claims.picture

      if (!publicData.displayName) return

      if (existing) {
        let needsUpdate = false
        const rec = existing
        for (const [k, v] of Object.entries(publicData)) {
          if (rec[k] !== v) {
            needsUpdate = true
            break
          }
        }
        if (!needsUpdate) return
        await this.deps.db.users.update(claims.sub, publicData)
      } else {
        await this.deps.db.users.add({
          id: claims.sub,
          email: claims.email ?? '',
          displayName: publicData.displayName as string,
          givenName: publicData.givenName as string | undefined,
          familyName: publicData.familyName as string | undefined,
          picture: publicData.picture as string | undefined,
          provider: 'google',
          createdAt: new Date().toISOString(),
          version: 0,
        } as unknown as import('../../schemas/user.js').User)
      }

      await this.deps.db.syncEvents.add({
        id: crypto.randomUUID(),
        entityType: 'user',
        entityId: claims.sub,
        operation: existing ? 'update' : 'create',
        data: {
          ...publicData,
          email: claims.email ?? '',
          ...(claims.phone_number ? { phone: claims.phone_number } : {}),
        },
        timestamp: new Date().toISOString(),
        conflictResolved: false,
      })
      log.info('User profile queued for sync')
    } catch (err) {
      log.warn('Failed to ensure user record:', err)
    }
  }

  private async restoreCachedState(): Promise<void> {
    try {
      const stored = await this.deps.db.authTokens.get('refresh')
      if (stored?.value) {
        this._hasPriorSession = true
      }

      const cached = await this.deps.db.authTokens.get('cached-auth-state')
      if (cached?.value) {
        const state: AuthState = JSON.parse(cached.value)
        if (state.status === 'authenticated' && state.userId) {
          log.debug('Restored cached identity for', state.userId)
          this.setState(state)
          return
        }
      }

      // Migration: refresh token exists but no cached state (pre-deploy session).
      // Derive identity from the local users table.
      if (this._hasPriorSession) {
        const users = await this.deps.db.users.toArray()
        const nonGuest = users.filter(u => u.id && u.id !== GUEST_USER_ID)
        if (nonGuest.length === 1) {
          const u = nonGuest[0]
          const state: AuthState = {
            status: 'authenticated',
            userId: u.id as string,
            ...(u.displayName ? { displayName: u.displayName as string } : {}),
            ...(u.email ? { email: u.email as string } : {}),
            ...(u.givenName ? { givenName: u.givenName as string } : {}),
            ...(u.familyName ? { familyName: u.familyName as string } : {}),
            ...(u.picture ? { picture: u.picture as string } : {}),
          }
          log.debug('Migrated cached identity from users table for', u.id)
          this.setState(state)
          await this.cacheAuthState(state)
        }
      }
    } catch (error) {
      log.debug('Failed to restore cached auth state:', error)
    }
  }

  private async cacheAuthState(state: AuthState): Promise<void> {
    try {
      localStorage.setItem(CACHED_AUTH_LS_KEY, JSON.stringify(state))
    } catch {}
    try {
      await this.deps.db.authTokens.put({
        id: 'cached-auth-state',
        value: JSON.stringify(state),
      })
    } catch (error) {
      log.debug('Failed to cache auth state:', error)
    }
  }

  private async clearCachedState(): Promise<void> {
    try {
      localStorage.removeItem(CACHED_AUTH_LS_KEY)
    } catch {}
    try {
      await this.deps.db.authTokens.delete('cached-auth-state')
    } catch (error) {
      log.debug('Failed to clear cached auth state:', error)
    }
  }

  private async refreshAccessToken(refreshToken: string): Promise<void> {
    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: this.deps.config.clientId,
      refresh_token: refreshToken,
    })

    let response: Response
    try {
      response = await this.deps.fetch(this.deps.config.tokenUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
        signal: AbortSignal.timeout(5000),
      })
    } catch (error) {
      // Network error or timeout — keep refresh token and cached identity
      // so the user can still operate locally. Retry on next app launch or
      // when the scheduled refresh timer fires.
      log.debug('Token refresh network error (keeping cached session):', error)
      return
    }

    if (!response.ok) {
      log.warn('Refresh token expired or revoked, clearing session')
      this.clearSession()
      return
    }

    const tokens: TokenResponse = await response.json()
    await this.processTokens(tokens)
  }

  private scheduleRefresh(expiresInSeconds: number): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer)
    }

    const delayMs = Math.max(0, expiresInSeconds * 1000 - REFRESH_BUFFER_MS)
    log.debug(`Scheduling token refresh in ${Math.round(delayMs / 1000)}s`)

    this.refreshTimer = setTimeout(() => {
      void (async () => {
        try {
          const stored = await this.deps.db.authTokens.get('refresh')
          if (stored?.value) {
            await this.refreshAccessToken(stored.value)
          }
        } catch (error) {
          log.error('Silent refresh failed:', error)
        }
      })()
    }, delayMs)
  }

  private async revokeToken(token: string): Promise<void> {
    const body = new URLSearchParams({
      token,
      client_id: this.deps.config.clientId,
    })

    await this.deps.fetch(this.deps.config.revokeUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    })
  }

  private parseIdToken(idToken: string): IdTokenClaims {
    return parseIdToken(idToken)
  }

  private clearSession(): void {
    this.accessToken = null
    this._idToken = null

    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer)
      this.refreshTimer = null
    }

    // Clear stored refresh token and cached identity
    this.deps.db.authTokens.delete('refresh').catch(err => {
      log.error('Failed to clear stored refresh token:', err)
    })
    void this.clearCachedState()

    this.setState(GUEST_STATE)
  }

  private setState(newState: AuthState): void {
    this.state = newState
    for (const listener of this.listeners) {
      try {
        listener(newState)
      } catch (err) {
        log.error('Auth state listener error:', err)
      }
    }
  }
}

export const authService = new AuthService()
